/**
 * @module components/ui/app-loader
 * @description Premium 60fps enterprise loading component featuring dual concentric
 * orbital rings, glowing primary aura, brand typography, and smooth kinetic micro-animations.
 */

import * as React from "react";
import { ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { APP_NAME } from "@/config/env";

interface AppLoaderProps {
  message?: string;
  submessage?: string;
  fullScreen?: boolean;
  className?: string;
}

export function AppLoader({
  message = "Loading platform...",
  submessage = "Verifying secure session & enterprise policies",
  fullScreen = true,
  className,
}: AppLoaderProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center bg-background text-foreground select-none",
        fullScreen ? "fixed inset-0 z-50 min-h-screen w-screen" : "w-full py-12",
        className
      )}
    >
      {/* Ambient background glowing orb */}
      <div className="relative flex items-center justify-center">
        <div className="absolute h-28 w-28 rounded-full bg-zinc-500/10 blur-2xl animate-pulse" />

        {/* Outer orbital spinning SVG ring */}
        <svg
          className="h-20 w-20 animate-spin text-foreground stroke-current"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="32"
            cy="32"
            r="28"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="130"
            strokeDashoffset="45"
            className="opacity-90"
          />
          <circle
            cx="32"
            cy="32"
            r="28"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            className="opacity-20"
          />
        </svg>

        {/* Inner reverse-spinning decorative ring */}
        <svg
          className="absolute h-14 w-14 animate-[spin_3s_linear_infinite_reverse] text-muted-foreground stroke-current"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="24"
            cy="24"
            r="20"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="40 20"
            strokeLinecap="round"
          />
        </svg>

        {/* Center brand squircle emblem */}
        <div className="absolute flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 shadow-md ring-2 ring-background">
          <ShieldCheck className="h-5 w-5 stroke-[2] animate-pulse" />
        </div>
      </div>

      {/* Typography & Kinetic Feedback */}
      <div className="mt-6 flex flex-col items-center text-center space-y-1.5 px-4 max-w-sm">
        <span className="text-sm font-semibold tracking-tight text-foreground">
          {APP_NAME}
        </span>
        <p className="text-xs font-medium text-foreground/90">
          {message}
        </p>
        {submessage && (
          <p className="text-[11px] text-muted-foreground font-mono">
            {submessage}
          </p>
        )}

        {/* Kinetic 3-dot wave */}
        <div className="flex items-center gap-1.5 pt-2">
          <span className="h-1.5 w-1.5 rounded-full bg-foreground animate-[bounce_1s_infinite_100ms]" />
          <span className="h-1.5 w-1.5 rounded-full bg-foreground animate-[bounce_1s_infinite_200ms]" />
          <span className="h-1.5 w-1.5 rounded-full bg-foreground animate-[bounce_1s_infinite_300ms]" />
        </div>
      </div>
    </div>
  );
}

/**
 * Reusable inline loader for tables, cards, and modal bodies.
 */
export function LoadingState({
  message = "Loading data...",
  className,
}: {
  message?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center p-8 space-y-3 text-muted-foreground select-none",
        className
      )}
    >
      <div className="relative flex items-center justify-center">
        <div className="h-9 w-9 rounded-full bg-zinc-500/10 blur-sm animate-pulse" />
        <svg
          className="h-8 w-8 animate-spin text-foreground stroke-current"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="16"
            cy="16"
            r="12"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="50"
            strokeDashoffset="18"
          />
        </svg>
      </div>
      <p className="text-xs font-medium text-muted-foreground font-mono">
        {message}
      </p>
    </div>
  );
}
